import React from 'react';
import {
  Box, Paper, Typography, IconButton, Tooltip, Chip, Divider, Drawer,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack,
  Tabs, Tab, Checkbox, Snackbar, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Autocomplete,
  FormControlLabel, MenuItem, LinearProgress, CircularProgress
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import Client from './components/Client';
import Config from './components/ClientConfig';
import ListsAcquisitionFilters from './components/ListsAcquisitionFilters';
import Avatar from '@mui/material/Avatar';
import noImage from './images/noImage.png';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

import {
  todayISO, nowLocalDT, csvCell, downloadBlob,
  toCurrencyCode, toCurrencyName,
  isUserTyping,
  acqProductsOf as _acqProductsOf,
  makeProdKey as _makeProdKey,
  stableProdKey as _stableProdKey,
  filterByQuery as _filterByQuery,
  filterByOverlap as _filterByOverlap,
  enrichAcquisitionCounts,
  exportAcquisitionLatestCSV as sharedExportLatestCSV,
  exportAcquisitionDetailCSV as sharedExportDetailCSV
} from './components/ListsShared';

// ===== ACQ: endpoints (adjust names if needed) =====
const PRODUCTS_ENDPOINT = (Config.API_URL_PRODUCT);
const UPDATE_META_ENDPOINT = () => `${Config.API_URL_LIST_ACQUISITION}update_meta/`;
const CREATE_EMPTY_ENDPOINT = () => `${Config.API_URL_LIST_ACQUISITION}create_empty/`;
const NEW_VERSION_ENDPOINT  = () => `${Config.API_URL_LIST_ACQUISITION}new_version/`;
const LIST_BASE_ENDPOINT    = () => Config.API_URL_LIST_ACQUISITION;

export default class ListsAcquisition extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      client: new Client('none'),
      companies: {},
      lists: [],
      rawList: [],
      loading: false,
      readLoading: false,
      error: '',

      drawerOpen: false,
      drawerCompany: null,
      drawerVersions: [],
      drawerLoading: false,

      histLimit: 10,
      histActiveOnly: false,
      histVersion: '',
      _histTimer: null,

      readOpen: false,
      readDetail: null,
      readTab: 0,

      // items edit
      readEditMode: false,
      readEdits: { products: {} },
      readAdds:   { products: [] },
      readRemovals: { products: new Set() },
      savingItems: false,

      // meta edit (no bump)
      editList: false,
      metaDirty: false,
      metaDateFrom: '',
      metaDateTo: '',
      metaIsActive: true,
      metaDescription: '',
      metaCurrency: 'RON',
      metaWithVAT: true,
      metaCreatedOn: '',
      metaCompany: '',

      // preloaded products catalog
      allProducts: [],
      prodOptions: [],
      prodSelected: null,
      prodAddQuery: '',
      prodPriceInput: '',
      prodActiveInput: true,

      qFilters: (()=>{ try{ const saved = JSON.parse(localStorage.getItem('la_qFilters')||'null'); if(saved&&saved.dateFrom&&saved.dateTo) return saved; }catch(e){} return { company: '', dateFrom: todayISO(), dateTo: todayISO() }; })(),
      showDrafts: JSON.parse(localStorage.getItem('la_showDrafts') || 'false'),

      snack: { open:false, message:'', severity:'success' },
      csvImporting: false,
      confirmCloseOpen:false,

      // compare previous (products only)
      cmpOpen: false,
      cmpBase: null,     // current version detail
      cmpOther: null,    // previous version detail
      cmpDiff: null,     // computed diff
      cmpIdx: null       // small index for displayName/displaySku
    };
  }

  STATUS_LABELS = {
    0: 'Registered',
    1: 'Idle',
    2: 'Active',
    9: 'Discontinued',
  };
  acqProductsOf = (detail) => _acqProductsOf(detail);
  makeProdKey    = (it, idx) => _makeProdKey(it, idx);
  stableProdKey  = (row) => _stableProdKey(row);
  filterByOverlap= (...args) => _filterByOverlap(...args);

  // Normalize a product line (catalog hydration + shapes)
  normalizeProductItem = (it, parent, idx) => {
    if (typeof it === 'string' || typeof it === 'number') it = { productRef: { iref: String(it) } };

    const idCands = [
      it?.product?.iref, it?.productRef?.iref,
      it?.product, it?.product_id, it?.productId, it?.productID,
      it?.product_iref, it?.productIref,
      it?.p_id, it?.p_iref,
      it?.sku, it?.code
    ].filter(Boolean).map(String);

    const products = this.state.allProducts || [];
    const ref = products.find(p =>
      idCands.includes(String(p.iref)) || (p.sku && idCands.includes(String(p.sku)))
    );

    const key = this.makeProdKey(it, idx);
    let name = it?.name || it?.product?.name || it?.productRef?.name || (ref?.name || '');
    let sku  = it?.sku  || it?.product?.sku  || it?.productRef?.sku  || (ref?.sku  || '');

    const price = this.parsePrice(it?.price ?? it?.value);

    let img = it?.img;
    if (!img && ref?.imgList?.length) img = this.getThumbFromImages(ref.imgList);
    if (!img) img = noImage;

    const pCategory    = it?.pCategory    || ref?.pCategory    || null;
    const pSubCategory = it?.pSubCategory || ref?.pSubCategory || null;
    const unitType     = it?.unitType     || ref?.unitType     || null;

    const currency = it?.currency ?? parent?.currency;
    const isPurchased = it?.isPurchased ?? ref?.isPurchased ?? null;
    const rawStatus = it?.status ?? ref?.status ?? null;
    const status = this.STATUS_LABELS[rawStatus] ?? '—';

    return {
      ...it,
      key, name, sku, price, currency,
      isActive: (it?.isActive != null ? !!it.isActive : (it?.active != null ? !!it.active : true)),
      unitType, pCategory, pSubCategory, img, isPurchased, status,
    };
  };

  componentDidMount(){
    try {
      localStorage.removeItem('la_qFilters');
      localStorage.removeItem('la_showDrafts');
      localStorage.removeItem('lc_copy_defaults');
      localStorage.removeItem('lc_showDrafts');
      localStorage.removeItem('lc_qFilters');
    } catch {}
    this.loadCompanies();
    this.preloadProducts();
    // initial fetch
    this.fetchLatest(this.buildQueryFromFilters());
    window.addEventListener('keydown', this.onKeyDown);
  }
  componentWillUnmount(){ window.removeEventListener('keydown', this.onKeyDown); }
  onKeyDown = (e) => { if (isUserTyping()) return; /* no keyboard shortcuts for ACQ */ };

  getOnlyCompanyIref = () => {
    const vals = Object.values(this.state.companies || {});
    return (vals.length === 1 ? vals[0]?.iref : '');
  };

  loadCompanies = () => {
    try {
      // 1) read internal supplier company iref from config
      let internalSupplier = null;
      try {
        const cfgReq = { key: 'INTERNAL_SUPPLIER_COMPANY_IREF' };
        const cfgRes = this.state.client.getData(Config.API_URL_CONFIG_SALESAPP, cfgReq);
        internalSupplier =
          cfgRes?.SalesAppConfigs?.[0]?.value ??
          cfgRes?.value ??
          cfgRes?.INTERNAL_SUPPLIER_COMPANY_IREF ??
          null;
        if (internalSupplier != null) internalSupplier = String(internalSupplier);
      } catch (e) {
        // leave internalSupplier as null if config not found
      }
    
      // 2) fetch companies (try server-side filter first)
      let res = null;
      if (internalSupplier) {
        try { res = this.state.client.getData(Config.API_URL_COMPANY, { iref: internalSupplier }); } catch {}
      }
      if (!res) {
        try { res = this.state.client.getData(Config.API_URL_COMPANY, {}); } catch {}
      }
    
      const arr = Array.isArray(res)
        ? res
        : (res?.Companies || res?.companies || res?.results || res?.company || []);
    
      // 3) normalize map
      const map = {};
      (arr || []).forEach(c => {
        const id = String(c?.iref ?? c?.id ?? c?.pk ?? '');
        if (!id) return;
        map[id] = { iref: id, name: (c?.name ?? c?.label ?? id) };
      });
    
      // 4) reduce to a single company if config present
      let companies = map;
      if (internalSupplier) {
        const one =
          map[internalSupplier] ||
          Object.values(map).find(cc => String(cc.iref) === internalSupplier);
        companies = one ? { [one.iref]: one } : {};
      }
    
      // 5) preset filters with that company (if available)
      const nextQ = { ...(this.state.qFilters || {}) };
      if (internalSupplier) nextQ.company = internalSupplier;
    
      this.setState({ companies, qFilters: nextQ });
      try { localStorage.setItem('la_qFilters', JSON.stringify(nextQ)); } catch {}
    } catch {
      this.setState({ companies: {} });
    }
  };

  preloadProducts = () => {
    try {
      const pr = this.state.client.getData(PRODUCTS_ENDPOINT, {});
      const products = Array.isArray(pr?.results) ? pr.results : (Array.isArray(pr) ? pr : (pr?.products || []));
      const mapped = (products || []).map(p => {
        const imgList = p.productImage_product || p.images || p.img || [];
        const pCat = p.pCategory || p.category || null;
        const pSub = p.pSubCategory || p.subCategory || null;
        return {
          iref: p.iref || p.id || p.sku || p.code || p.uuid,
          id: p.id ?? null,
          code: p.code ?? null,
          uuid: p.uuid ?? null,
          name: p.name || '',
          sku: p.sku || '',
          unitType: p.unitType,
          pCategory: pCat ? { name: pCat.name, image: pCat.image } : null,
          pSubCategory: pSub ? { name: pSub.name, image: pSub.image } : null,
          imgList,
          isPurchased: p.isPurchased ?? p.isSellable ?? null,
          status: p.status ?? null,
          label: p.name || '',
        };
      }).filter(o => o.iref)
      .sort((a,b)=> (a.name||'').localeCompare(b.name||''));
      this.setState({ allProducts: mapped, prodOptions: mapped.slice(0,50) });
    } catch {}
  };

  applyFilters = (queryData) => {
    const q = { ...(queryData || {}) };
    if (q.currency != null && typeof q.currency !== 'number') q.currency = toCurrencyCode(q.currency);
    if (q.withVAT != null) q.withVAT = (q.withVAT === true || q.withVAT === 'true' || q.withVAT === 1 || q.withVAT === '1') ? 1 : 0;
    if (q.status != null) q.status = (q.status === true || q.status === 'true' || q.status === 1 || q.status === '1') ? 1 : 0;
    if (!q.dateFrom) q.dateFrom = todayISO();
    if (!q.dateTo) q.dateTo = todayISO();
    this.setState({ qFilters: q }, ()=>{
      try { localStorage.setItem('la_qFilters', JSON.stringify(q)); } catch {}
      this.fetchLatest(this.buildQueryFromFilters());
    });
  };

  buildQueryFromFilters = () => {
    const q = { ...(this.state?.qFilters || {}) };
    if (!q.company) {
      const only = this.getOnlyCompanyIref();
      if (only) q.company = only;
    }
    if (!this.state.showDrafts) {
      if (q.status == null) q.status = 1;
      if (q.dateFrom && q.dateTo) q.overlap = 1;
      delete q.all_versions;
      return q;
    }
    q.all_versions = 1;
    if (q.dateFrom && q.dateTo) q.overlap = 1;
    return q;
  };

  fetchLatest = (overrides) => {
    this.setState({ loading: true, error: '' });

    const q = { ...(this.state?.qFilters || {}), ...(overrides || {}) };
    if (!q.company) {
      const only = this.getOnlyCompanyIref();
      if (only) q.company = only;
    }
    const { dateFrom, dateTo } = q;
    const showDrafts = !!this.state?.showDrafts;

    // Build a lean server query — NO location in ACQ
    const query = {
      thin: 1,
      fields: 'iref,version,isActive,currency,withVAT,dateFrom,dateTo,company,description,productCount'
    };

    if (q.company) query.company = q.company;
    if (q.currency !== undefined && q.currency !== '') query.currency = toCurrencyCode(q.currency);
    if (q.withVAT !== undefined) query.withVAT = !!q.withVAT;

    if (showDrafts || q.all_versions) {
      query.all_versions = 1;
      query.history_limit = this.state.histLimit || 10;
    } else {
      if (q.status == null) query.status = 1;
      if (dateFrom && dateTo) {
        query.dateFrom = dateFrom;
        query.dateTo = dateTo;
        query.overlap = 1;
        query.latest_per_track = 1;
      }
    }

    try {
      const res    = this.state.client.getData(LIST_BASE_ENDPOINT(), query);
      const arrRaw = Array.isArray(res) ? res : (res?.results || res?.acquisitionList || []);
      const arr    = (arrRaw || []).map(r => ({
        ...r,
        withVAT: (r?.withVAT === true || r?.withVAT === 1 || r?.withVAT === '1' || String(r?.withVAT).toLowerCase() === 'true'),
        currency: (Number.isInteger(r?.currency) ? r.currency : toCurrencyCode(r?.currency)),
      }));

      // Decide which set to show (track = company|currency|VAT)
      let lists;
      if (q.all_versions && !showDrafts) {
        lists = this.latestPlusActivePerTrack(arr);
      } else if (dateFrom && dateTo && showDrafts) {
        lists = (arr.some(r => r && r.version) ? arr : this.groupLatestAcq(arr));
      } else {
        lists = this.groupLatestAcq(arr);
      }

      this.setState({ rawList: arr, lists, loading: false }, this.hydrateCounts);
    } catch (e) {
      this.setState({ error: 'Failed to load acquisition lists', loading: false });
    }
  };

  groupLatestAcq = (rows) => {
    const map = new Map();
    (rows || []).forEach((x) => {
      const key = `${x?.company?.iref || x?.company}|${x?.currency}|${!!x?.withVAT}`;
      const m = map.get(key);
      if (!m || Number(x.version) > Number(m.version)) map.set(key, x);
    });
    return Array.from(map.values());
  };

  latestPlusActivePerTrack = (rows) => {
    const byTrack = new Map();
    (rows || []).forEach(r => {
      const key = `${r.company?.iref || r.company}|${r.currency}|${!!r.withVAT}`;
      const arr = byTrack.get(key) || [];
      arr.push(r);
      byTrack.set(key, arr);
    });
    const out = [];
    byTrack.forEach((arr) => {
      const sorted = arr.slice().sort((a,b)=> Number(b.version) - Number(a.version));
      const latest = sorted[0];
      const latestActive = sorted.find(x => !!x.isActive);
      if (latest) out.push(latest);
      if (latestActive && latestActive.iref !== latest.iref) out.push(latestActive);
    });
    return out.sort((a,b)=>{
      const ca = (a.company?.name || a.company || '').toString().toLowerCase();
      const cb = (b.company?.name || b.company || '').toString().toLowerCase();
      if (ca < cb) return -1; if (ca > cb) return 1;
      return Number(b.version) - Number(a.version);
    });
  };


  mergeVersionsByIref = (...lists) => {
    const map = new Map();
    lists.flat().filter(Boolean).forEach((v) => {
      const key = String(v.iref || `${v.version}_${v.dateFrom}_${v.dateTo}`);
      const old = map.get(key) || {};
      map.set(key, { ...old, ...v });
    });
    return Array.from(map.values()).sort((a, b) => Number(b.version || 0) - Number(a.version || 0));
  };

  markTimelineRows = (versions, searchedVersion = '') => {
    const searched = String(searchedVersion || '').trim();
    return (versions || []).map(v => ({
      ...v,
      _isSearchedVersion: searched !== '' && String(v.version) === searched,
    }));
  };

  // HISTORY
  fetchHistoryForCompany = () => {
    const { histLimit = 10, histCtx } = this.state;

    const comp = histCtx?.companyIref || this.state.drawerCompany?.iref || this.state.drawerCompany;
    if (!comp) return;

    const q = this.state.qFilters || {};
    const selectedFrom = q.dateFrom || todayISO();
    const selectedTo = q.dateTo || selectedFrom;

    const baseParams = {
      company: comp,
      currency: toCurrencyCode(histCtx?.currency),
      withVAT: !!histCtx?.withVAT,
      all_versions: 1,
      history_limit: histLimit,
    };

    const searchedVersion = String(this.state.histVersion || '').trim();

    this.setState({ drawerLoading: true });

    try {
      // 1) Load normal period context.
      // This is important because effective version must be calculated against
      // all relevant active versions, not only the searched version.
      const periodParams = {
        ...baseParams,
        dateFrom: selectedFrom,
        dateTo: selectedTo,
        overlap: 1,
        history_limit: Math.max(Number(histLimit || 10), 30),
      };

      const periodRes = this.state.client.getData(LIST_BASE_ENDPOINT(), periodParams);
      const periodList = Array.isArray(periodRes)
        ? periodRes
        : (periodRes?.results || periodRes?.acquisitionList || []);

      let searchedList = [];

      // 2) If user searched a version, load it separately and merge it into the timeline.
      if (searchedVersion !== '') {
        const searchParams = {
          ...baseParams,
          exact_version: Number(searchedVersion),
          history_limit: 1,
        };

        const searchRes = this.state.client.getData(LIST_BASE_ENDPOINT(), searchParams);
        searchedList = Array.isArray(searchRes)
          ? searchRes
          : (searchRes?.results || searchRes?.acquisitionList || []);
      }

      const merged = this.mergeVersionsByIref(periodList, searchedList)
        .map(enrichAcquisitionCounts);

      this.setState({
        drawerVersions: this.markTimelineRows(merged, searchedVersion),
        drawerLoading: false
      });
    } catch {
      this.setState({ drawerLoading: false });
    }
  };

  openHistory = (row) => {
    const comp = row?.company?.iref || row?.company;
    const cur = row?.currency;
    const vat = !!row?.withVAT;

    this.setState({
      drawerOpen: true,
      drawerCompany: row.company,
      drawerVersions: [],
      drawerLoading: false,
      histLimit: 10,
      histActiveOnly: false,
      histVersion: '',
      histCtx: {
        companyIref: comp,
        currency: cur,
        withVAT: vat
      }
    }, this.fetchHistoryForCompany);
  };

  getDetailByIref = (iref) => {
    try { return this.state.client.getData(`${LIST_BASE_ENDPOINT()}${iref}/`, {}); }
    catch (e) { return null; }
  };

  hydrateDetailWithCatalogs = (detail) => {
    if (!detail) return detail;
    const pArr = this.state.allProducts || [];
    const idx = new Map();
    pArr.forEach(p => {
      if (p.iref) idx.set(String(p.iref), p);
      if (p.id != null) idx.set(String(p.id), p);
      if (p.sku) idx.set(String(p.sku), p);
      if (p.code) idx.set(String(p.code), p);
      if (p.uuid) idx.set(String(p.uuid), p);
    });

    const arrayize = (listLike) => {
      if (Array.isArray(listLike)) return listLike;
      const out = [];
      const obj = (listLike || {});
      Object.entries(obj).forEach(([k, v]) => {
        if (v == null) return;
        if (typeof v === 'object') {
          out.push({ ...v, productRef: v.productRef || v.product || { iref: String(k) } });
        } else {
          const price = this.parsePrice(v);
          out.push({ productRef: { iref: String(k) }, price });
        }
      });
      return out;
    };

    const fixed = arrayize(detail.productAcquisition_list).map(it => {
      if (typeof it === 'string' || typeof it === 'number') {
        const id = String(it);
        const ref = idx.get(id);
        return ref ? { productRef: { iref: ref.iref, name: ref.name, sku: ref.sku } } : { product: id };
      }
      const idCands = [
        it?.product?.iref, it?.productRef?.iref,
        it?.product, it?.product_id, it?.productId, it?.productID,
        it?.product_iref, it?.productIref, it?.p_id, it?.p_iref,
        it?.sku, it?.code
      ].filter(Boolean).map(String);
      let ref = null;
      for (const id of idCands) { ref = idx.get(id); if (ref) break; }
      return (ref && (!it.product && !it.productRef))
        ? { ...it, productRef: { iref: ref.iref, name: ref.name, sku: ref.sku } }
        : it;
    });

    return { ...detail, productAcquisition_list: fixed };
  };

  ensureDetailItems = (iref, attempt = 0) => {
    const d = this.state.readDetail;
    const hasLines = Array.isArray(d?.productAcquisition_list) ? d.productAcquisition_list.length
                    : (d?.productAcquisition_list ? Object.keys(d.productAcquisition_list).length : 0);
    const haveNames = (this.acqProductsOf(d) || []).some(it => it?.product?.name || it?.productRef?.name || it?.name);
    const havePrice = (this.acqProductsOf(d) || []).some(it => {
      const v = (it?.price != null ? it.price : it?.value);
      return v != null && !Number.isNaN(Number(v));
    });
    if ((hasLines && haveNames && havePrice) || attempt >= 5) return;

    setTimeout(() => {
      const fresh = this.getDetailByIref(iref);
      const hydrated = this.hydrateDetailWithCatalogs(fresh);
      const enriched = enrichAcquisitionCounts(hydrated);
      this.setState({
        readDetail: enriched,
        metaDateFrom: enriched.dateFrom || '',
        metaDateTo:   enriched.dateTo || '',
        metaIsActive: !!enriched.isActive,
        metaDescription: enriched.description || '',
        metaCurrency: toCurrencyName(enriched.currency),
        metaWithVAT:  !!enriched.withVAT,
        metaCreatedOn: enriched.createdOn || '',
        metaCompany: (enriched.company?.iref || enriched.company || '')
      }, () => this.ensureDetailItems(iref, attempt + 1));
    }, Math.min(250 + attempt * 250, 1500));
  };

  openRead = (row, opts = {}) => {
    const forceHydrate = !!opts.forceHydrate;
    const key = row?.iref || row?.id || row?.pk;
    if (!key) return;

    this.setState({ readOpen: true, readLoading: true, readDetail: null }, () => {
      let detail = row;
      const hasAny = !!(detail && detail.productAcquisition_list);
      if (forceHydrate || !hasAny) {
        const fresh = this.getDetailByIref(key);
        if (fresh) detail = fresh;
      }
      detail = this.hydrateDetailWithCatalogs(detail);
      const enriched = enrichAcquisitionCounts(detail);
      this.setState({
        readDetail: enriched,
        readLoading: false,
        metaDateFrom: enriched.dateFrom || '',
        metaDateTo:   enriched.dateTo || '',
        metaIsActive: !!enriched.isActive,
        metaDescription: enriched.description || '',
        metaCurrency: toCurrencyName(enriched.currency),
        metaWithVAT:  !!enriched.withVAT,
        metaCreatedOn: enriched.createdOn || '',
        metaCompany: (enriched.company?.iref || enriched.company || '')
      }, () => this.ensureDetailItems(key));
    });
  };

  handleAttemptCloseRead = () => {
    const hasItemEdits =
      Object.keys(this.state.readEdits.products || {}).length ||
      Object.keys(this.state.readEdits.combos || {}).length ||
      (this.state.readAdds.products?.length) ||
      (this.state.readAdds.combos?.length) ||
      (this.state.readRemovals.products?.size) ||
      (this.state.readRemovals.combos?.size);

    const hasMetaEdits = !!this.state.metaDirty;

    if (hasItemEdits || hasMetaEdits) {
      this.setState({ confirmCloseOpen: true }); // reuse your confirm dialog
    } else {
      this.setState({ readOpen: false });
    }
  };

  // ---- META SAVE (no new version) ----
  saveMeta = (closeAfter=false) => {
    const d = this.state.readDetail;
    if (!d || !this.state.metaDirty) return;
    const iref = String(d.iref || '');
    if (!iref) {
      this.setState({ snack:{ open:true, message:'Missing iref for meta update.', severity:'error' } });
      return;
    }
    const body = {
      iref,
      dateFrom: this.state.metaDateFrom ?? d.dateFrom ?? '',
      dateTo:   this.state.metaDateTo   ?? d.dateTo   ?? '',
      isActive: this.state.metaIsActive ? 1 : 0,
      description: this.state.metaDescription ?? (d.description || ''),
    };
    if (this.state.metaCompany && this.state.metaCompany !== (d.company?.iref || d.company)) {
      body.company = String(this.state.metaCompany);
    }
    if (this.state.metaCurrency !== undefined && this.state.metaCurrency !== null && this.state.metaCurrency !== '') {
      const code = toCurrencyCode(this.state.metaCurrency);
      if (Number.isInteger(code)) body.currency = String(code);
    }
    if (this.state.metaWithVAT === true || this.state.metaWithVAT === false) {
      body.withVAT = this.state.metaWithVAT ? 1 : 0;
    }

    let res = null;
    try { res = this.state.client.pushData(UPDATE_META_ENDPOINT(), body); }
    catch { res = null; }

    if (!res || res.status === 'error' || res.detail) {
      this.setState({ snack: { open:true, message: (res?.detail || 'Meta update failed.'), severity:'error' } });
      return;
    }

    const merged = {
      ...d,
      dateFrom: body.dateFrom,
      dateTo:   body.dateTo,
      isActive: !!body.isActive,
      description: body.description,
      currency: body.currency != null ? Number(body.currency) : d.currency,
      withVAT:  (body.withVAT != null ? !!body.withVAT : d.withVAT),
      company: body.company ? body.company : d.company,
    };
    this.setState({
      readDetail: merged,
      metaDateFrom: merged.dateFrom,
      metaDateTo:   merged.dateTo,
      metaCompany: (merged.company?.iref || merged.company || ''),
      metaIsActive: !!merged.isActive,
      metaDescription: merged.description,
      metaCurrency: toCurrencyName(merged.currency),
      metaWithVAT:  !!merged.withVAT,
      metaCreatedOn: merged.createdOn,
      metaDirty:false, editList:false,
      snack: { open:true, message:'Updated.', severity:'success' },
      readOpen: (closeAfter ? false : this.state.readOpen),
    }, () => {
      this.fetchLatest(this.buildQueryFromFilters());
    });
  };

  // ---- items edit & add ----
  setEdit = (bucket, key, field, value) => {
    this.setState((prev)=>{
      const edits = { ...prev.readEdits };
      const obj = { ...(edits[bucket][key] || {}) };
      obj[field] = value;
      edits[bucket] = { ...edits[bucket], [key]: obj };
      return { readEdits: edits };
    });
  };
  toggleRemoval = (bucket, key) => {
    this.setState((prev)=>{
      const s = new Set(prev.readRemovals[bucket]);
      if (s.has(key)) s.delete(key); else s.add(key);
      return { readRemovals: { ...prev.readRemovals, [bucket]: s } };
    });
  };
  removeNewRow = (bucket, tempIndex) => {
    this.setState((prev)=>{
      const arr = [...prev.readAdds[bucket]];
      arr.splice(tempIndex,1);
      return { readAdds: { ...prev.readAdds, [bucket]: arr } };
    });
  };

  filterProducts = (q) => {
    const src = this.state.allProducts;
    if (!q) return src.slice(0,50);
    const qq = q.toLowerCase();
    return src.filter(o =>
      (o.name && o.name.toLowerCase().includes(qq)) ||
      (o.sku && o.sku.toLowerCase().includes(qq)) ||
      (o.iref && String(o.iref).toLowerCase().includes(qq))
    ).slice(0,50);
  };
  matchesAddQuery = (item, q) => {
    const qq = String(q || '').trim().toLowerCase();
    if (!qq) return true;
    return (
      String(item?.name || '').toLowerCase().includes(qq) ||
      String(item?.sku || '').toLowerCase().includes(qq) ||
      String(item?.iref || '').toLowerCase().includes(qq) ||
      String(item?.product_iref || '').toLowerCase().includes(qq) ||
      String(item?.product_name || '').toLowerCase().includes(qq)
    );
  };
  productIdOf = (it) => (
    it?.product?.iref ||
    it?.productRef?.iref ||
    it?.product ||
    it?.product_id ||
    it?.product_iref ||
    it?.productId ||
    it?.productID ||
    it?.productIref ||
    null
  );
  existsInCurrent = (iref) => {
    const detail = this.state.readDetail;
    if (!detail || !iref) return false;
    const found = this.acqProductsOf(detail).some((it, idx) => {
      const item = this.normalizeProductItem(it, detail, idx);
      return String(this.productIdOf(item)) === String(iref);
    });
    const inAdds = (this.state.readAdds.products || []).some(n =>
      String(n.product_iref) === String(iref)
    );
    return found || inAdds;
  };
  addNewProduct = () => {
    const s = this.state.prodSelected;
    const price = this.state.prodPriceInput;
    if (!s || !s.iref || price === '' || isNaN(Number(price)) || Number(price) < 0) {
      this.setState({ snack:{ open:true, message:'Select a product and enter a numeric price.', severity:'warning' } });
      return;
    }
    if (this.existsInCurrent(s.iref, false)) {
      this.setState({ snack:{ open:true, message:'Product already in this list.', severity:'info' } });
      return;
    }
    this.setState((prev)=> ({
      readAdds: {
        ...prev.readAdds,
        products: [
          ...prev.readAdds.products,
          {
            product_iref: s.iref,
            product_name: s.name,
            price: Number(price),
            isActive: !!prev.prodActiveInput,
            isPurchased: s.isPurchased,
            status: this.STATUS_LABELS[s.status] ?? '—',
          }
        ],
      },
      prodSelected: null,
      prodAddQuery: '',
      prodPriceInput: '',
      prodOptions: prev.allProducts.slice(0,50),
    }));
  };

  getThumbFromImages = (imgList) => {
    try {
      if (!imgList || !imgList.length) return null;
      let src = imgList[0]?.image;
      for (let i = 0; i < imgList.length; i++) {
        if (imgList[i]?.isMain === true) { src = imgList[i].image; break; }
      }
      if (!src) return null;
      const fileName = src.replace(/\.[^/.]+$/, "");
      const ext = src.split(".").pop();
      return `${fileName}_s.${ext}`;
    } catch(e) { return null; }
  };

  nameOf = (val, listOrMap) =>
    typeof val === 'object'
      ? (val?.name || '—')
      : Array.isArray(listOrMap)
        ? (listOrMap.find(x => String(x.iref) === String(val))?.name || String(val))
        : (listOrMap?.[String(val)]?.name || String(val) || '—');

  parsePrice = (v) => {
    if (v === null || v === undefined) return undefined;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const s = String(v).trim().replace(/\s+/g,'').replace(',', '.');
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
  };

  // Save (apply diff) — ACQ has only products
  saveNewVersionFromEdits = (closeAfter=false) => {
    this.setState({ savingItems: true, closeAfterSave: closeAfter });

    const { readDetail, readEdits, readAdds, readRemovals } = this.state;
    if (!readDetail) return;

    const prodChanges = [];
    this.acqProductsOf(readDetail).forEach((it, idx) => {
      const key = this.makeProdKey(it, idx);
      const chg = readEdits.products[key];
      const prodIref = it.product?.iref || it.productRef?.iref;
      if (!prodIref) return;

      if (this.state.readRemovals.products.has(key)) {
        prodChanges.push({ product_iref: prodIref, remove: true });
        return;
      }
      if (!chg) return;

      const payload = { product_iref: prodIref };
      if (chg.price != null && chg.price !== '' && !Number.isNaN(Number(chg.price))) {
        payload.price = Math.max(0, Number(chg.price));
      }
      if (chg.isActive != null) payload.isActive = !!chg.isActive;
      if (payload.price !== undefined || payload.isActive !== undefined) prodChanges.push(payload);
    });

    (readAdds.products || []).forEach(n => {
      const prodIref = n?.product_iref || n?.iref;
      if (!prodIref) return;
      const payload = { product_iref: prodIref, add: true };
      if (n.price != null && n.price !== '' && !Number.isNaN(Number(n.price))) {
        payload.price = Math.max(0, Number(n.price));
      }
      if (n.isActive != null) payload.isActive = !!n.isActive;
      prodChanges.push(payload);
    });

    if (prodChanges.length === 0) {
      this.setState({
        savingItems: false,
        snack: { open: true, message: 'No changes to save.', severity: 'info' }
      });
      return;
    }

    const payload = {
      company: (readDetail.company?.iref || readDetail.company),
      currency: (typeof toCurrencyCode === 'function' ? toCurrencyCode(readDetail.currency) : readDetail.currency),
      withVAT: !!readDetail.withVAT,
      from_acquisition_version: Number(readDetail.version),   // ACQ: backend accepts this
      product_changes: prodChanges,
    };

    let res = null;
    try { res = this.state.client.pushData(NEW_VERSION_ENDPOINT(), payload); }
    catch { res = null; }

    if (!res || res.status === 'error' || res.detail) {
      this.setState({ savingItems: false, snack: { open: true, message: 'Could not save via API. Exported changes JSON.', severity: 'warning' } });
      downloadBlob(JSON.stringify(payload, null, 2), `acquisition_changes_${todayISO()}.json`, 'application/json');
      return;
    }

    const wasMaterialized = (res.mode === 'materialized');
    const msg = res?.message || (wasMaterialized ? 'Saved items.' : 'New version created');

    this.setState({
      snack: { open: true, message: msg, severity: 'success' },
      readEditMode: false,
      readEdits: { products: {} },
      readAdds: { products: [] },
      readRemovals: { products: new Set() }
    });

    const newIref = res.iref;

    // Refresh the track (company,currency,vat)
    this.fetchLatest({ company: payload.company, currency: payload.currency, withVAT: payload.withVAT, all_versions: 1 });

    if (closeAfter) {
      this.setState({ savingItems: false, readOpen: false, closeAfterSave: false });
      return;
    }

    // Keep dialog open on the new version
    let detail = null;
    try { detail = this.state.client.getData(`${LIST_BASE_ENDPOINT()}${newIref}/`, {}); } catch {}
    if (detail) this.openRead(detail, { forceHydrate: true });
    this.setState({ savingItems: false, closeAfterSave: false });
  };

  handleCreateEmpty = (payload) => {
    const body = {
      ...payload,
      company: payload.company || this.getOnlyCompanyIref(),
      currency: toCurrencyCode(payload.currency),
      withVAT: !!payload.withVAT,
      isActive: !!payload.isActive
    };
    let res = null;
    try { res = this.state.client.pushData(CREATE_EMPTY_ENDPOINT(), body); }
    catch { res = null; }
    if (!res || res.status === 'error' || res.detail) {
      this.setState({ snack:{ open:true, message:'Could not create via API. Exported JSON.', severity:'warning' } });
      downloadBlob(JSON.stringify(body, null, 2), `acquisition_new_${todayISO()}.json`, 'application/json');
      return;
    }
    this.setState({ snack:{ open:true, message: res?.message || 'Acquisition list created.', severity:'success' } });
    const newIref = res?.iref || res?.data?.iref;
    const cur = body.currency; const vat = !!body.withVAT; const comp = body.company;
    this.setState(prev => ({ qFilters: { ...prev.qFilters, company: comp, currency: cur, withVAT: vat } }));
    this.fetchLatest({ company: comp, currency: cur, withVAT: vat, all_versions: 1 });
    if (newIref) {
      const detail = this.getDetailByIref(newIref);
      if (detail) this.openRead(detail);
    }
  };

  // ---- CSV import for empty/new acquisition list versions ----
  getExistingAcquisitionItemCount = (detail = this.state.readDetail) => {
    if (!detail) return 0;

    if (Array.isArray(detail.productAcquisition_list)) {
      return detail.productAcquisition_list.length;
    }

    if (detail.productAcquisition_list && typeof detail.productAcquisition_list === 'object') {
      return Object.keys(detail.productAcquisition_list).length;
    }

    const countFields = [
      detail._prodCount,
      detail.productCount,
      detail.productsCount,
    ];

    return countFields.reduce((max, v) => {
      const n = Number(v);
      return Number.isFinite(n) ? Math.max(max, n) : max;
    }, 0);
  };

  canImportAcquisitionCsv = () => {
    const d = this.state.readDetail;
    if (!d || !d.iref) return false;

    const pendingAdds = (this.state.readAdds?.products || []).length;

    return this.getExistingAcquisitionItemCount(d) === 0 && pendingAdds === 0;
  };

  parseCsvLine = (line) => {
    const out = [];
    let cur = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      const next = line[i + 1];

      if (ch === '"' && inQuotes && next === '"') {
        cur += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        out.push(cur.trim());
        cur = '';
      } else {
        cur += ch;
      }
    }

    out.push(cur.trim());
    return out;
  };

  parseAcquisitionImportCsv = (text) => {
    const lines = String(text || '')
      .replace(/^\uFEFF/, '')
      .split(/\r?\n/)
      .map(l => l.trim())
      .filter(Boolean);

    if (!lines.length) return { rows: [], errors: ['CSV file is empty.'] };

    const header = this.parseCsvLine(lines[0]).map(h => h.toLowerCase());
    const irefIdx = header.indexOf('iref');
    const priceIdx = header.indexOf('price');

    if (irefIdx < 0 || priceIdx < 0) {
      return { rows: [], errors: ['CSV header must contain: iref, price.'] };
    }

    const rows = [];
    const errors = [];
    const seen = new Set();

    lines.slice(1).forEach((line, idx) => {
      const lineNo = idx + 2;
      const cols = this.parseCsvLine(line);
      const iref = String(cols[irefIdx] || '').trim();
      const rawPrice = String(cols[priceIdx] || '').trim().replace(',', '.');
      const price = Number(rawPrice);

      if (!iref) {
        errors.push(`Line ${lineNo}: missing iref.`);
        return;
      }

      if (rawPrice === '' || !Number.isFinite(price) || price < 0) {
        errors.push(`Line ${lineNo}: invalid price.`);
        return;
      }

      if (seen.has(iref)) {
        errors.push(`Line ${lineNo}: duplicate product ${iref}.`);
        return;
      }

      seen.add(iref);
      rows.push({ iref, price });
    });

    return { rows, errors };
  };

  downloadAcquisitionImportTemplate = () => {
    const sample = [
      'iref,price',
      'PRODUCT_IREF_HERE,19.90',
      'PRODUCT_IREF_HERE_2,12.50',
    ].join('\n');

    downloadBlob(
      sample,
      `acquisition_list_import_template_${todayISO()}.csv`,
      'text/csv;charset=utf-8;'
    );
  };

  handleAcquisitionCsvSelected = (e) => {
    const file = e?.target?.files?.[0];
    if (e?.target) e.target.value = '';
    if (!file) return;

    if (!this.canImportAcquisitionCsv()) {
      this.setState({
        snack: {
          open: true,
          message: 'CSV import is available only for empty acquisition list versions.',
          severity: 'warning'
        }
      });
      return;
    }

    this.setState({ csvImporting: true });

    const reader = new FileReader();

    reader.onload = () => {
      const { rows, errors } = this.parseAcquisitionImportCsv(reader.result || '');

      const productsByIref = new Map(
        (this.state.allProducts || []).map(p => [String(p.iref), p])
      );

      const productAdds = [];
      const validationErrors = [...errors];

      rows.forEach((row) => {
        const product = productsByIref.get(String(row.iref));

        if (!product) {
          validationErrors.push(`Product not found: ${row.iref}`);
          return;
        }

        productAdds.push({
          product_iref: product.iref,
          product_name: product.name,
          price: row.price,
          isActive: true,
          isPurchased: product.isPurchased,
          status: this.STATUS_LABELS[product.status] ?? '—',
        });
      });

      if (validationErrors.length) {
        const body = validationErrors
          .map(x => `"${String(x).replace(/"/g, '""')}"`)
          .join('\n');

        downloadBlob(
          `error\n${body}`,
          `acquisition_list_import_errors_${todayISO()}.csv`,
          'text/csv;charset=utf-8;'
        );

        this.setState({
          csvImporting: false,
          snack: {
            open: true,
            message: 'CSV import has errors. An error file was downloaded.',
            severity: 'error'
          }
        });

        return;
      }

      if (!productAdds.length) {
        this.setState({
          csvImporting: false,
          snack: {
            open: true,
            message: 'No valid rows found in CSV.',
            severity: 'warning'
          }
        });

        return;
      }

      this.setState((prev) => ({
        csvImporting: false,
        readEditMode: true,
        readAdds: {
          ...prev.readAdds,
          products: [...(prev.readAdds.products || []), ...productAdds],
        },
        snack: {
          open: true,
          message: `Imported ${productAdds.length} products. Review and save items to apply.`,
          severity: 'success'
        }
      }));
    };

    reader.onerror = () => {
      this.setState({
        csvImporting: false,
        snack: {
          open: true,
          message: 'Could not read CSV file.',
          severity: 'error'
        }
      });
    };

    reader.readAsText(file);
  };

  exportAcqDetailCSV = () => {
    const detail = this.state.readDetail;
    return sharedExportDetailCSV(detail, {
      productsOf: (d) => this.acqProductsOf(d),
      normalizeProductItem: (it, d, i) => this.normalizeProductItem(it, d, i),
      toCurrencyName, csvCell, downloadBlob, todayISO,
    });
  };
  exportAcqLatestCSV = (row = null) => {
    const lists = row?.iref
      ? [row]
      : (this.state.lists || []);

    if (!lists.length) return;
    if (!this.state.allProducts?.length) {
      this.preloadProducts?.();
    }

    const helpers = {
      getDetailByIref: (id) => this.getDetailByIref(id),
      hydrateDetailWithCatalogs: (d) => this.hydrateDetailWithCatalogs(d),
      productsOf: (d) => this.acqProductsOf(d),
      normalizeProductItem: (it, d, i) => this.normalizeProductItem(it, d, i),
    };

    sharedExportLatestCSV(lists, helpers, { withDetails: true });
  };

  hydrateCounts = () => {
    const lists = this.state.lists || [];
    const maxFetch = Math.min(lists.length, 20);
    let changed = false;
    const updated = lists.map((r, idx) => {
      if (idx >= maxFetch) return r;
      if ((r.productCount != null || r._prodCount != null)) return r;
      if (!r?.iref) return r;
      try {
        const det = this.state.client.getData(`${LIST_BASE_ENDPOINT()}${r.iref}/`, {});
        const pc = Array.isArray(det?.productAcquisition_list) ? det.productAcquisition_list.length
                 : (det?.productAcquisition_list ? Object.keys(det.productAcquisition_list).length : 0);
        changed = true;
        return { ...r, productCount: pc, _prodCount: pc };
      } catch { return r; }
    });
    if (changed) this.setState({ lists: updated });
  };

  hasUnsavedChanges = () => {
    const hasEdits = Object.keys(this.state.readEdits.products || {}).length > 0;
    const hasAdds  = (this.state.readAdds.products || []).length > 0;
    const hasRem   = (this.state.readRemovals.products || new Set()).size > 0;
    return hasEdits || hasAdds || hasRem || !!this.state.metaDirty;
  };

  parseVersion = (v) => {
    if (typeof v === 'number') return v;
    const m = String(v || '').match(/\d+/);
    return m ? Number(m[0]) : NaN;
  };

  arrayizeProductList = (listLike) => {
    if (Array.isArray(listLike)) return listLike;
    const out = [];
    const obj = (listLike || {});
    Object.entries(obj).forEach(([k, v]) => {
      if (v == null) return;
      if (typeof v === 'object') {
        out.push({ ...v, productRef: v.productRef || v.product || { iref: String(k) } });
      } else {
        const price = this.parsePrice(v);
        out.push({ productRef: { iref: String(k) }, price });
      }
    });
    return out;
  };

  // small compare-name index from the two details
  buildNameIndex = (details) => {
    const prod = new Map();
    (details || []).forEach((d) => {
      (this.acqProductsOf(d) || []).forEach((row, i) => {
        const it = this.normalizeProductItem(row, d, i);
        const id = it?.product?.iref || it?.productRef?.iref || it?.sku;
        if (id) prod.set(String(id), { name: it.name, sku: it.sku });
      });
    });
    return { prod };
  };

  displayName = (r) => {
    const direct = r?.name || r?.product?.name || r?.productRef?.name;
    if (direct) return direct;
    const ids = [
      r?.product?.iref, r?.productRef?.iref, r?.product, r?.product_id, r?.productID,
      r?.productIref, r?.product_iref, r?.p_id, r?.p_iref, r?.sku, r?.code
    ].filter(Boolean).map(String);
    // try small idx first
    for (const id of ids) {
      const hit = this.state.cmpIdx?.prod?.get(id);
      if (hit?.name) return hit.name;
    }
    // try preloaded catalog
    for (const id of ids) {
      const p = (this.state.allProducts || []).find(x =>
        String(x.iref) === id || String(x.id) === id || String(x.sku) === id || String(x.code) === id || String(x.uuid) === id
      );
      if (p?.name) return p.name;
    }
    return r?.sku || r?.product?.iref || r?.productRef?.iref || '—';
  };

  displaySku = (r) => {
    const direct = r?.sku || r?.product?.sku || r?.productRef?.sku;
    if (direct) return direct;
    const ids = [
      r?.product?.iref, r?.productRef?.iref, r?.product, r?.product_id, r?.productID,
      r?.productIref, r?.product_iref, r?.p_id, r?.p_iref, r?.code
    ].filter(Boolean).map(String);
    for (const id of ids) {
      const p = (this.state.allProducts || []).find(x =>
        String(x.iref) === id || String(x.id) === id || String(x.code) === id || String(x.uuid) === id
      );
      if (p?.sku) return p.sku;
    }
    return '';
  };

  buildDiff = (base, other) => {
    const bP = this.arrayizeProductList(base?.productAcquisition_list).map((it, i) => this.normalizeProductItem(it, base, i));
    const oP = this.arrayizeProductList(other?.productAcquisition_list).map((it, i) => this.normalizeProductItem(it, other, i));
    const stableKey = this.stableProdKey;
    const aMap = new Map(bP.map(x => [stableKey(x), x]));
    const bMap = new Map(oP.map(x => [stableKey(x), x]));
    const keys = new Set([...aMap.keys(), ...bMap.keys()]);
    const added = [], removed = [], changed = [];
    keys.forEach(k => {
      const x = aMap.get(k); // current
      const y = bMap.get(k); // previous
      if (x && !y) { added.push({ ...x, _idKey: k }); return; }
      if (!x && y) { removed.push({ ...y, _idKey: k }); return; }
      if (x && y) {
        const px = (x?.price == null ? null : this.parsePrice(x.price));
        const py = (y?.price == null ? null : this.parsePrice(y.price));
        const ax = !!x.isActive;
        const ay = !!y.isActive;
        if (px !== py || ax !== ay) {
          changed.push({
            _idKey: k,
            from: { name: y.name, sku: y.sku, price: py, isActive: ay },
            to:   { name: x.name, sku: x.sku, price: px, isActive: ax }
          });
        }
      }
    });
    return {
      products: {
        summary: { added: added.length, removed: removed.length, changed: changed.length,
                  total: added.length + removed.length + changed.length },
        added, removed, changed
      }
    };
  };

  getHydratedDetailForCompare = async (rowOrIref) => {
    const id = (typeof rowOrIref === 'string' || typeof rowOrIref === 'number')
      ? String(rowOrIref)
      : (rowOrIref?.iref || rowOrIref?.id || '');
    if (!id) return rowOrIref;
    // ensure products catalog (names/SKU in diff)
    if (!this.state.allProducts?.length) this.preloadProducts?.();
    const raw = this.getDetailByIref(id) || rowOrIref;
    return this.hydrateDetailWithCatalogs(raw);
  };

  openComparePrev = async (row) => {
    if (!row) return;
    // identify the track (company × currency × VAT)
    const comp = row.company?.iref || row.company;
    const cur  = (Number.isInteger(row.currency) ? row.currency : toCurrencyCode(row.currency));
    const vat  = !!row.withVAT;
  
    // ask backend for thin history on this track
    const params = {
      company: comp,
      currency: cur,
      withVAT: vat,
      all_versions: 1,
      history_limit: 10,
      thin: 1,
      fields: 'iref,version,company,currency,withVAT'
    };
    const res = this.state.client.getData(LIST_BASE_ENDPOINT(), params);
    const rawList = Array.isArray(res) ? res : (res?.results || res?.acquisitionList || []);
    const list = (rawList || []).map(r => ({
      ...r,
      withVAT: (r?.withVAT === true || r?.withVAT === 1 || r?.withVAT === '1' ||
                String(r?.withVAT).toLowerCase() === 'true'),
      currency: (Number.isInteger(r?.currency) ? r.currency : toCurrencyCode(r?.currency)),
      _v: this.parseVersion(r?.version),
    })).filter(r => Number.isFinite(r._v))
      .sort((a,b)=> b._v - a._v);
  
    const byVersion = new Map();
    for (const r of list) if (!byVersion.has(r._v)) byVersion.set(r._v, r);
    const uniq = Array.from(byVersion.values());
  
    const baseV = this.parseVersion(row.version);
    const base = uniq.find(x => x._v === baseV);
    const prev = uniq.find(x => x._v < baseV);
    if (!base || !prev) {
      this.setState({ snack:{ open:true, message:'No previous version for this track.', severity:'info' } });
      return;
    }
  
    const baseDet = await this.getHydratedDetailForCompare(base.iref || base.id || base);
    const prevDet = await this.getHydratedDetailForCompare(prev.iref || prev.id || prev);
    const cmpIdx  = this.buildNameIndex([baseDet, prevDet]);
    const diff    = this.buildDiff(baseDet, prevDet);
    this.setState({ cmpOpen:true, cmpBase:baseDet, cmpOther:prevDet, cmpDiff:diff, cmpIdx });
  };
  // ---------- change detection helpers (aligned with ListsPrice) ----------
  handleCloseRead = () => {
    if (this.hasUnsavedChanges()) {
      this.setState({ confirmCloseOpen:true });
    } else {
      this.setState({ readOpen:false });
    }
  };
  discardChangesAndClose = () => {
    this.setState({
      readOpen:false,
      confirmCloseOpen:false,
      readEditMode:false,
      readEdits:{ products:{}, combos:{} },
      readAdds:{ products:[], combos:[] },
      readRemovals:{ products:new Set(), combos:new Set() },
      metaDirty:false,
      editPriceList:false,
    });
  };
  hasItemChanges = () => {
    if (!this.state.readEditMode) return false;
    const hasEdits = Object.keys(this.state.readEdits?.products || {}).length > 0;
    const hasAdds  = (this.state.readAdds?.products || []).length > 0;
    const hasRem   = (this.state.readRemovals?.products || new Set()).size > 0;
    return hasEdits || hasAdds || hasRem;
  };
  hasMetaChanges = () => !!this.state.metaDirty;
  hasUnsavedChanges = () => this.hasItemChanges() || this.hasMetaChanges();

  // ---------- VERSION TIMELINE ----------
  addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  toISODate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  parseISODateOnly = (value) => {
    if (!value) return null;
    const s = String(value).slice(0, 10);
    const d = new Date(`${s}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  diffDaysInclusive = (from, to) => {
    if (!from || !to) return 0;
    const ms = 24 * 60 * 60 * 1000;
    return Math.floor((to.getTime() - from.getTime()) / ms) + 1;
  };

  getSelectedTimelinePeriod = () => {
    const q = this.state.qFilters || {};

    let from = this.parseISODateOnly(q.dateFrom);
    let to = this.parseISODateOnly(q.dateTo);

    if (!to) to = this.parseISODateOnly(todayISO());
    if (!from) from = new Date(to);

    if (from > to) {
      const tmp = from;
      from = to;
      to = tmp;
    }

    return {
      from,
      to,
      totalDays: this.diffDaysInclusive(from, to)
    };
  };

  getTimelinePeriod = () => {
    const selected = this.getSelectedTimelinePeriod();

    const selectedDays = selected.totalDays || 1;

    let visualFrom;
    let visualTo;

    if (selectedDays <= 1) {
      // One selected day: show a compact 3-month window around it
      visualFrom = this.addDays(selected.from, -14);
      visualTo = this.addDays(selected.from, 76);
    } else if (selectedDays <= 31) {
      // Short selected period: keep context but not too much
      visualFrom = this.addDays(selected.from, -14);
      visualTo = this.addDays(selected.to, 45);
    } else {
      // Larger selected period: show selected range + small context
      visualFrom = this.addDays(selected.from, -7);
      visualTo = this.addDays(selected.to, 14);
    }

    const totalDays = this.diffDaysInclusive(visualFrom, visualTo);

    return {
      from: visualFrom,
      to: visualTo,
      selectedFrom: selected.from,
      selectedTo: selected.to,
      totalDays,
    };
  };

  clampDate = (date, min, max) => {
    if (!date) return null;
    if (date < min) return min;
    if (date > max) return max;
    return date;
  };

  versionIsValidOnDay = (version, day) => {
    const from = this.parseISODateOnly(version?.dateFrom);
    const to = this.parseISODateOnly(version?.dateTo) || new Date('2999-12-31T00:00:00');

    if (!from) return false;

    return from <= day && day <= to;
  };

  getEffectiveVersionByDay = (versions, day) => {
    const activeVersions = (versions || [])
      .filter(v => !!v.isActive)
      .filter(v => this.versionIsValidOnDay(v, day))
      .sort((a, b) => Number(b.version || 0) - Number(a.version || 0));

    return activeVersions[0] || null;
  };

  buildVersionTimelineRows = () => {
    const versions = this.state.drawerVersions || [];
    const { from, to } = this.getTimelinePeriod();

    const totalDays = this.diffDaysInclusive(from, to);
    if (!versions.length || totalDays <= 0) {
      return { rows: [], from, to, totalDays };
    }

    const rows = versions
      .slice()
      .sort((a, b) => Number(b.version || 0) - Number(a.version || 0))
      .map((version) => {
        const vFromRaw = this.parseISODateOnly(version.dateFrom);
        const vToRaw = this.parseISODateOnly(version.dateTo) || to;

        if (!vFromRaw) {
          return {
            version,
            hasOverlap: false,
            leftPct: 0,
            widthPct: 0,
            activeSegments: [],
          };
        }

        const start = this.clampDate(vFromRaw, from, to);
        const end = this.clampDate(vToRaw, from, to);
        const hasOverlap = start && end && start <= end;

        if (!hasOverlap) {
          return {
            version,
            hasOverlap: false,
            leftPct: 0,
            widthPct: 0,
            activeSegments: [],
          };
        }

        const leftDays = Math.max(0, this.diffDaysInclusive(from, start) - 1);
        const widthDays = this.diffDaysInclusive(start, end);

        const leftPct = (leftDays / totalDays) * 100;
        const widthPct = (widthDays / totalDays) * 100;

        const activeSegments = [];
        let segStart = null;

        for (let i = 0; i < totalDays; i += 1) {
          const day = this.addDays(from, i);
          const effective = this.getEffectiveVersionByDay(versions, day);
          const isThisEffective =
            effective &&
            String(effective.iref || effective.version) === String(version.iref || version.version);

          if (isThisEffective && !segStart) {
            segStart = day;
          }

          if ((!isThisEffective || i === totalDays - 1) && segStart) {
            const segEnd = isThisEffective && i === totalDays - 1
              ? day
              : this.addDays(day, -1);

            const segLeftDays = Math.max(0, this.diffDaysInclusive(from, segStart) - 1);
            const segWidthDays = this.diffDaysInclusive(segStart, segEnd);

            activeSegments.push({
              from: segStart,
              to: segEnd,
              leftPct: (segLeftDays / totalDays) * 100,
              widthPct: (segWidthDays / totalDays) * 100,
            });

            segStart = null;
          }
        }

        const selected = this.getSelectedTimelinePeriod();
        const isEffectiveInSelectedPeriod = activeSegments.some(seg => {
          const segFrom = seg.from;
          const segTo = seg.to;
          return segFrom <= selected.to && segTo >= selected.from;
        });

        return {
          version,
          hasOverlap,
          leftPct,
          widthPct,
          activeSegments,
          validFrom: start,
          validTo: end,
          isEffectiveInSelectedPeriod,
        };
      });

    return { rows, from, to, totalDays };
  };

  buildTimelineTicks = (from, to) => {
    const totalDays = this.diffDaysInclusive(from, to);
    if (!from || !to || totalDays <= 0) return [];

    const ticks = [];
    const current = new Date(from);

    const day = current.getDay();
    const daysToMonday = day === 0 ? 1 : (8 - day) % 7;
    current.setDate(current.getDate() + daysToMonday);

    let tickIndex = 0;

    while (current <= to) {
      const leftDays = Math.max(0, this.diffDaysInclusive(from, current) - 1);

      ticks.push({
        date: new Date(current),
        leftPct: (leftDays / totalDays) * 100,
        label: current.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short'
        }),
        showLabel: tickIndex % 2 === 0,
      });

      current.setDate(current.getDate() + 7);
      tickIndex += 1;
    }

    return ticks;
  };

  getSelectedRangeStyle = (from, to, selectedFrom, selectedTo, totalDays) => {
    if (!from || !to || !selectedFrom || !selectedTo || totalDays <= 0) return null;

    const start = this.clampDate(selectedFrom, from, to);
    const end = this.clampDate(selectedTo, from, to);

    if (!start || !end || start > end) return null;

    const leftDays = Math.max(0, this.diffDaysInclusive(from, start) - 1);
    const widthDays = Math.max(1, this.diffDaysInclusive(start, end));

    return {
      leftPct: (leftDays / totalDays) * 100,
      widthPct: Math.max(0.8, (widthDays / totalDays) * 100),
    };
  };

  renderVersionTimeline = () => {
    const { rows, from, to, totalDays } = this.buildVersionTimelineRows();
    const visualPeriod = this.getTimelinePeriod();
    const ticks = this.buildTimelineTicks(from, to);

    const selectedStyle = this.getSelectedRangeStyle(
      from,
      to,
      visualPeriod.selectedFrom,
      visualPeriod.selectedTo,
      totalDays
    );

    if (!rows.length) return null;

    const effectiveVersions = rows
      .filter(r => r.isEffectiveInSelectedPeriod)
      .map(r => `v${r.version.version}`);
    const searchedRows = rows.filter(r => r.version._isSearchedVersion);
    const searchedNotEffective = searchedRows.some(r => !r.isEffectiveInSelectedPeriod);

    const selectedLabel = `${visualPeriod.selectedFrom.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} — ${visualPeriod.selectedTo.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
    const periodLabel = `${from.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} — ${to.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          mb: 1.25,
          borderRadius: 2,
          bgcolor: 'background.paper',
          borderColor: 'divider'
        }}
      >
        <Stack spacing={0.75}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Version timeline
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                {periodLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                Selected: {selectedLabel}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2, color: searchedNotEffective ? 'warning.main' : 'text.secondary' }}>
                Effective for selected period: {effectiveVersions.length ? effectiveVersions.join(', ') : 'none'}
                {searchedNotEffective ? ' · searched version is not effective for this period' : ''}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 18, height: 8, borderRadius: 99, bgcolor: 'success.main' }} />
                <Typography variant="caption" color="text.secondary">Effective</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 18, height: 8, borderRadius: 99, bgcolor: 'primary.light', opacity: 0.55 }} />
                <Typography variant="caption" color="text.secondary">Valid</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 18, height: 8, borderRadius: 0.75, bgcolor: 'warning.light', opacity: 0.35 }} />
                <Typography variant="caption" color="text.secondary">Selected period</Typography>
              </Stack>
            </Stack>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '74px 1fr',
              columnGap: 1,
              alignItems: 'end'
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
              Ver.
            </Typography>

            <Box sx={{ position: 'relative', height: 18 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ position: 'absolute', left: 0, top: 0, fontSize: 10 }}
              >
                {from.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ position: 'absolute', right: 0, top: 0, fontSize: 10 }}
              >
                {to.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
              </Typography>

              {ticks.map((tick, idx) => {
                if (!tick.showLabel) return null;
                if (tick.leftPct < 8 || tick.leftPct > 92) return null;

                return (
                  <Typography
                    key={`${tick.label}_${idx}`}
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      position: 'absolute',
                      left: `${tick.leftPct}%`,
                      top: 0,
                      transform: 'translateX(-50%)',
                      fontSize: 9,
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', sm: 'block' }
                    }}
                  >
                    {tick.label}
                  </Typography>
                );
              })}
            </Box>
          </Box>

          <Stack spacing={0.4}>
            {rows.map((row, idx) => {
              const v = row.version;
              const isEffectiveInPeriod = row.isEffectiveInSelectedPeriod;
              const isSearchedVersion = !!v._isSearchedVersion;

              return (
                <Box
                  key={v.iref || `${v.version}_${idx}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '74px 1fr',
                    columnGap: 1,
                    alignItems: 'center',
                    minHeight: 28
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 800,
                          color: isEffectiveInPeriod ? 'success.main' : 'text.primary',
                          lineHeight: 1.05
                        }}
                      >
                        v{v.version}
                      </Typography>

                      {isSearchedVersion && (
                        <Chip
                          size="small"
                          label="Searched"
                          variant="outlined"
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      )}

                      {isEffectiveInPeriod && (
                        <Chip
                          size="small"
                          label="Effective"
                          color="success"
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      )}

                      {v.isActive && !isEffectiveInPeriod && (
                        <Chip
                          size="small"
                          label="Enabled"
                          variant="outlined"
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      )}
                    </Stack>
                  </Box>

                  <Tooltip
                    arrow
                    title={
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                          Version v{v.version}
                        </Typography>

                        <Typography variant="caption" sx={{ display: 'block' }}>
                          Validity: {v.dateFrom || '—'} → {v.dateTo || '∞'}
                        </Typography>

                        <Typography variant="caption" sx={{ display: 'block' }}>
                            Status: {v.isActive ? 'Enabled' : 'Draft / inactive'}
                          </Typography>

                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Selected period: {isEffectiveInPeriod ? 'Effective' : 'Not effective'}
                          </Typography>

                          {isSearchedVersion && (
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              Search result: yes
                            </Typography>
                          )}
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        height: 24,
                        borderRadius: 1.25,
                        bgcolor: 'action.hover',
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      {ticks.map((tick, tickIdx) => (
                        <Box
                          key={tickIdx}
                          sx={{
                            position: 'absolute',
                            left: `${tick.leftPct}%`,
                            top: 0,
                            bottom: 0,
                            width: '1px',
                            bgcolor: 'divider',
                            opacity: 0.7
                          }}
                        />
                      ))}

                      {selectedStyle && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${selectedStyle.leftPct}%`,
                            width: `${selectedStyle.widthPct}%`,
                            top: 0,
                            bottom: 0,
                            bgcolor: 'warning.light',
                            opacity: 0.28
                          }}
                        />
                      )}

                      {row.hasOverlap && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${row.leftPct}%`,
                            width: `${Math.max(row.widthPct, 0.8)}%`,
                            top: 7,
                            height: 10,
                            borderRadius: 99,
                            bgcolor: 'primary.light',
                            opacity: 0.55
                          }}
                        />
                      )}

                      {row.activeSegments.map((seg, segIdx) => (
                        <Box
                          key={segIdx}
                          sx={{
                            position: 'absolute',
                            left: `${seg.leftPct}%`,
                            width: `${Math.max(seg.widthPct, 0.8)}%`,
                            top: 5,
                            height: 14,
                            borderRadius: 99,
                            bgcolor: 'success.main',
                            boxShadow: 1
                          }}
                        />
                      ))}
                    </Box>
                  </Tooltip>
                </Box>
              );
            })}
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, lineHeight: 1.25 }}>
            <Box component="span" sx={{ display: 'block' }}>
              Effective = enabled version with the highest version number valid for each date.
            </Box>
            <Box component="span" sx={{ display: 'block' }}>
              A searched version is shown for reference, even when it is not effective in the selected period.
            </Box>
          </Typography>
        </Stack>
      </Paper>
    );
  };
  
  render(){
    const { lists, loading } = this.state;

    return (
      <div style={{ backgroundColor: '#273442', minHeight: '100%' }}>
        <div className='sticky'>
          <ListsAcquisitionFilters
            applyFilters={this.applyFilters}
            listsCounter={lists?.length || 0}
            companies={this.state.companies}
            lockCompany={Object.keys(this.state.companies || {}).length === 1}
            onCreateEmpty={this.handleCreateEmpty}
            sx={{ position: 'sticky', top: 0}}
          />
        </div>

        <Box sx={{ px: 1, py: 1 }}>
          <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
            <FormControlLabel
              control={<Checkbox checked={!!this.state.showDrafts} onChange={(e)=>{ const v=e.target.checked; this.setState({ showDrafts: v }, ()=>{ try{ localStorage.setItem('la_showDrafts', JSON.stringify(v)); }catch(e){} this.fetchLatest(this.buildQueryFromFilters()); }); }} />}
              label='Show drafts'
            />
            <Button size='small' variant='outlined' onClick={()=>{ const q={ dateFrom: todayISO(), dateTo: todayISO() }; this.setState({ qFilters: q }, ()=>{ try{ localStorage.setItem('la_qFilters', JSON.stringify(q)); }catch(e){} const p={ ...q }; if (!this.state.showDrafts) p.status=1; this.fetchLatest(p); }); }}>Valid today</Button>
            <Chip size='small' sx={{ bgcolor: "#455a76", color: "#fff" }} label={`Valid: ${(this.state.qFilters?.dateFrom)||'-'} — ${(this.state.qFilters?.dateTo)||'∞'}`} />
            {!this.state.showDrafts && <Chip size='small' label='Active only' color='primary' variant='outlined' />}
          </Stack>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: '68vh', borderRadius: 2, boxShadow: 3, overflow: 'auto' }}>
          <Table aria-label='latest table' size='small' stickyHeader>
            <caption style={{ captionSide:'top', backgroundColor:'#273442', color:'#FFF', fontWeight:'bold', textAlign:'left', padding:'8px 16px', fontSize:'1.1rem', borderTopLeftRadius:8, borderTopRightRadius:8 }}>
              Acquisition Lists
            </caption>
            <TableHead sx={{backgroundColor: '#273442', color: '#FFFFFF !important'}}>
              <TableRow sx={{backgroundColor: '#273442', color: '#FFFFFF !important'}}>
                <TableCell sx={{backgroundColor: '#273442', color: '#FFFFFF !important'}}>Company</TableCell>
                <TableCell sx={{backgroundColor: '#273442', color: '#FFFFFF !important'}}>Version</TableCell>
                <TableCell sx={{backgroundColor: '#273442', color: '#FFFFFF !important'}}>Items</TableCell>
                <TableCell sx={{backgroundColor: '#273442', color: '#FFFFFF !important'}}>Effective</TableCell>
                <TableCell align='right' sx={{backgroundColor: '#273442', color: '#FFFFFF !important', position:'sticky', right:0, zIndex: 3}}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                '& .MuiTableRow-root:nth-of-type(odd)': { bgcolor: 'rgba(255,255,255,0.03)' },
                '& .MuiTableRow-root:hover': { bgcolor: 'rgba(255,255,255,0.06) !important' },
              }}
            >
              {loading && (
                <TableRow><TableCell colSpan={4}>
                  <Stack alignItems='center' sx={{ py: 3 }}>
                    <Typography variant='body2' color='text.secondary'>Loading…</Typography>
                  </Stack>
                </TableCell></TableRow>
              )}
              {!loading && lists.map((r)=> (
                <TableRow key={String(r.iref)} hover sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' }, height:72 }}>
                  <TableCell>
                    <Chip label={r.company?.name || r.company} size='medium' sx={{ fontSize:'0.95rem', px:1.2, py:0.6 }}/>
                  </TableCell>
                  <TableCell>
                    <Stack direction='row' spacing={0.75} alignItems='center'>
                      <Chip label={`v${r.version}`} size='medium' color='primary' sx={{ fontSize:'0.85rem', px:1, py:0.4 }}/>
                      <Chip label={`${r.iref}`} size='medium' variant='outlined' sx={{ fontSize:'0.75rem', px:1, py:0.4 }}/>
                      <Chip label={toCurrencyName(r.currency)} size='medium' variant='outlined' sx={{ fontSize:'0.85rem', px:1, py:0.4 }}/>
                      <Chip label={r.withVAT? 'VAT':'NoVAT'} size='medium' variant='outlined' sx={{ fontSize:'0.85rem', px:1, py:0.4 }}/>
                      <Chip label={r.isActive ? 'Active' : 'Draft'} size='medium' variant={r.isActive ? 'filled' : 'outlined'} color={r.isActive ? 'success' : 'default'} sx={{ fontSize:'0.85rem', px:1, py:0.4 }}/>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip size='medium' label={`P:${(r.productCount ?? r._prodCount ?? 0)}`} sx={{ fontSize:'0.8rem', px:1 }}/>
                  </TableCell>
                  <TableCell>{`${r.dateFrom || ''} — ${r.dateTo || '∞'}`}</TableCell>
                  <TableCell align='right' sx={{ position:'sticky', right:0, backgroundColor: 'background.paper', zIndex: 2 }}>
                    <Stack direction='row' spacing={0.5} justifyContent='flex-end'>
                      <Tooltip title='View'><IconButton onClick={()=>this.openRead(r)}><VisibilityIcon fontSize='inherit'/></IconButton></Tooltip>
                      <Tooltip title='Compare prev'><IconButton onClick={()=>this.openComparePrev(r)}><CompareArrowsIcon fontSize='inherit'/></IconButton></Tooltip>
                      <Tooltip title='History'><IconButton onClick={()=>this.openHistory(r)}><HistoryIcon fontSize='inherit'/></IconButton></Tooltip>
                      <Tooltip title='Export CSV'>
                        <IconButton
                          onClick={(ev) => {
                            ev.stopPropagation();
                            this.exportAcqLatestCSV(r);
                          }}
                        >
                          <CloudDownloadIcon fontSize='inherit'/>
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && lists.length === 0 && (
                <TableRow><TableCell colSpan={4}>
                  <Stack alignItems='center' spacing={1} sx={{ py:2 }}>
                    <Typography variant='body2' color='text.secondary'>No acquisition lists for selected filters.</Typography>
                    <Button size='small' variant='outlined' onClick={()=> this.applyFilters({ dateFrom: todayISO(), dateTo: todayISO() })}>Jump to today</Button>
                  </Stack>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* History Drawer (by company / track = company×currency×VAT) */}
        <Drawer anchor='right' open={this.state.drawerOpen} onClose={()=>this.setState({ drawerOpen:false })}>
          <Box sx={{ width: { xs: '100vw', sm: 720, md: 820 }, p: 1.25 }}>
            <Stack direction='row' alignItems='center' justifyContent='space-between'>
              <Typography variant='subtitle1'>
                History — {this.state.drawerCompany?.name || this.state.drawerCompany || ''}
              </Typography>

              <IconButton size='small' onClick={()=>this.setState({ drawerOpen:false })}>
                <CloseIcon fontSize='small'/>
              </IconButton>
            </Stack>

            <Divider sx={{ my: 1 }} />

            <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={this.state.histActiveOnly}
                    onChange={(e)=> {
                      this.setState({ histActiveOnly: e.target.checked }, this.fetchHistoryForCompany);
                    }}
                  />
                }
                label='Active only'
              />

              <TextField
                size='small'
                label='Version #'
                value={this.state.histVersion}
                onChange={(e)=> {
                  const v = e.target.value;
                  this.setState({ histVersion: v });
                  clearTimeout(this.state._histTimer);
                  const t = setTimeout(this.fetchHistoryForCompany, 500);
                  this.setState({ _histTimer: t });
                }}
              />
            </Stack>

            {this.state.drawerLoading && <Typography variant='body2'>Loading…</Typography>}

            {!this.state.drawerLoading && this.renderVersionTimeline()}

            {!this.state.drawerLoading && this.state.drawerVersions.map((v, idx)=> (
              <Stack
                key={v.iref || `${v.version}_${idx}`}
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                sx={{ py: 0.75 }}
              >
                <Stack spacing={0.25}>
                  <Stack direction='row' spacing={0.75} flexWrap="wrap">
                    <Chip label={v.company?.name || v.company} size='small'/>
                    <Chip label={`v${v.version}`} size='small' color='primary'/>
                    <Chip label={`${v.iref}`} size='small' variant='outlined'/>
                    <Chip label={toCurrencyName(v.currency)} size='small' variant='outlined'/>
                    <Chip label={v.withVAT ? 'VAT' : 'NoVAT'} size='small' variant='outlined'/>
                    <Chip label={v.isActive ? 'Active' : 'Draft'} size='small' />
                  </Stack>

                  <Typography variant='caption'>
                    {v.dateFrom || ''} — {v.dateTo || '∞'} • P:{(v.productCount ?? v._prodCount ?? 0)}
                  </Typography>
                </Stack>

                <Stack direction='row' spacing={0.5}>
                  <Tooltip title='View'>
                    <IconButton size='small' onClick={()=>this.openRead(v)}>
                      <VisibilityIcon fontSize='inherit'/>
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            ))}
          </Box>
        </Drawer>

        {/* Details Dialog (Products only) */}
        <Dialog
          open={this.state.readOpen}
          fullWidth
          maxWidth='xl'
          PaperProps={{
            sx: {
              width: '90%',
              height: '90%',
              margin: 'auto',
            },
          }}
          disableEscapeKeyDown
          onClose={(_, reason) => {
            // ignore backdrop clicks
            if (reason === 'backdropClick') return;
            if (this.hasUnsavedChanges()) {
              this.setState({ confirmCloseOpen: true });
            } else {
              this.setState({ readOpen: false });
            }
          }}
        >
          <DialogTitle sx={{ pr: 6 }}>
            <IconButton
              aria-label="close"
              onClick={() => this.handleAttemptCloseRead()}
              sx={{ position:'absolute', right:8, top:8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogTitle sx={{ py: 1.25 }}>
            Acquisition List — {(this.state.readDetail?.company?.name || this.state.readDetail?.company)} · v{this.state.readDetail?.version} · {this.state.readDetail?.iref}  ({toCurrencyName(this.state.readDetail?.currency)}, {this.state.readDetail?.withVAT? 'VAT':'NoVAT'})
          </DialogTitle>
          <DialogContent dividers sx={{ p: 1.5, minHeight: 560 }}>
            {this.state.readDetail && (
              <>
                {/* Meta */}
                <Paper variant='outlined' sx={{ p: 1, mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <TextField
                      label='Company'
                      select
                      value={(this.state.metaCompany || (this.state.readDetail?.company?.iref || this.state.readDetail?.company) || '')}
                      onChange={(e)=> this.setState({ metaCompany: e.target.value, metaDirty: true })}
                      size='small' sx={{ minWidth: 220 }} disabled={!this.state.editList}
                    >
                      {Object.values(this.state.companies || {})
                        .filter(c=>c?.iref)
                        .sort((a,b)=> String(a.name||'').localeCompare(String(b.name||''), undefined, {sensitivity:'base'}))
                        .map(c => <MenuItem key={c.iref} value={c.iref}>{c.name || c.iref}</MenuItem>)}
                    </TextField>
                    <TextField label='From' type='date' value={this.state.metaDateFrom} onChange={(e)=> this.setState({ metaDateFrom: e.target.value, metaDirty:true })} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} size='small' disabled={!this.state.editList}/>
                    <TextField label='To'   type='date' value={this.state.metaDateTo}   onChange={(e)=> this.setState({ metaDateTo: e.target.value,   metaDirty:true })} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} size='small' disabled={!this.state.editList}/>
                    <TextField label='Description' value={this.state.metaDescription || ''} onChange={(e)=> this.setState({ metaDescription: e.target.value, metaDirty: true })} size='small' sx={{ minWidth: 240 }} disabled={!this.state.editList}/>
                    <TextField label='Currency' select value={this.state.metaCurrency || ''} onChange={(e)=> this.setState({ metaCurrency: e.target.value, metaDirty: true })} size='small' sx={{ minWidth: 80 }} disabled={!this.state.editList}>
                      <MenuItem value="RON">RON</MenuItem><MenuItem value="EUR">EUR</MenuItem><MenuItem value="USD">USD</MenuItem>
                    </TextField>
                    <Tooltip
                      title="Defines whether prices include VAT. Changing this will not create a new version, but will affect how prices are displayed and calculated."
                      arrow
                      placement="top"
                      disableFocusListener
                      disableInteractive
                      enterDelay={500}
                    >
                      <TextField
                        label="VAT"
                        select
                        size="small"
                        value={this.state.metaWithVAT ? 'vat' : 'no_vat'}
                        onChange={(e) => this.setState({
                          metaWithVAT: e.target.value === 'vat',
                          metaDirty: true
                        })}
                        disabled={!this.state.editList}
                        sx={{ minWidth: 115 }}
                      >
                        <MenuItem value="vat">With VAT</MenuItem>
                        <MenuItem value="no_vat">No VAT</MenuItem>
                      </TextField>
                    </Tooltip>
                    <FormControlLabel
                      control={<Checkbox checked={this.state.metaIsActive} onChange={(e)=> this.setState({ metaIsActive: e.target.checked, metaDirty:true })} disabled={!this.state.editList} />}
                      label='Active'
                    />
                    <Box flexGrow={1} />
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Tooltip title='Edit period / active'><IconButton size='small' onClick={()=> this.setState({ editList: !this.state.editList })}><EditIcon sx={{ color: this.state.editList ? '#8f241d' : '#e3e3e5' }} /></IconButton></Tooltip>
                      {this.state.editList && (
                        <>
                          <Button size='small' variant='outlined' onClick={()=> this.setState({
                            editList:false, metaDirty:false,
                            metaDateFrom: this.state.readDetail.dateFrom || todayISO(),
                            metaDateTo:   this.state.readDetail.dateTo || '',
                            metaIsActive: this.state.readDetail.isActive !== undefined ? !!this.state.readDetail.isActive : true,
                            metaDescription: this.state.readDetail.description || '',
                            metaCurrency: toCurrencyName(this.state.readDetail.currency),
                            metaWithVAT:  !!this.state.readDetail.withVAT
                          })}>Cancel</Button>
                          <Tooltip title='Save metadata (no new version)'>
                            <span>
                              <Button size='small' startIcon={<SaveIcon/>} variant='contained' disabled={!this.state.metaDirty} onClick={this.saveMeta}>Save</Button>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Paper>

                <Stack direction='row' alignItems='center' justifyContent='space-between'>
                  <Stack direction='row' spacing={2}>
                    <Typography variant='caption' color='text.secondary'>Effective: {this.state.readDetail.dateFrom || ''} — {this.state.readDetail.dateTo || '∞'}</Typography>
                    <Typography variant='caption' color='text.secondary'>Created on: {this.state.readDetail.createdOn || ''}</Typography>
                  </Stack>
                  <Stack direction='row' spacing={1} flexWrap='wrap' justifyContent='flex-end'>
                    {this.canImportAcquisitionCsv() && (
                      <>
                        <input
                          id="acquisition-list-csv-import"
                          type="file"
                          accept=".csv,text/csv"
                          style={{ display: 'none' }}
                          onChange={this.handleAcquisitionCsvSelected}
                        />

                        <Tooltip title="CSV columns: iref, price">
                          <Button
                            size='small'
                            variant='outlined'
                            startIcon={this.state.csvImporting ? <CircularProgress size={16} /> : <UploadFileIcon />}
                            onClick={() => document.getElementById('acquisition-list-csv-import')?.click()}
                            disabled={this.state.csvImporting}
                          >
                            Import CSV
                          </Button>
                        </Tooltip>

                        <Tooltip title="Download CSV template">
                          <Button
                            size='small'
                            variant='text'
                            startIcon={<CloudDownloadIcon />}
                            onClick={this.downloadAcquisitionImportTemplate}
                          >
                            Template
                          </Button>
                        </Tooltip>
                      </>
                    )}

                    {!this.state.readEditMode && (
                      <Button size='small' variant='contained' onClick={()=>this.setState({ readEditMode:true })}>Edit items</Button>
                    )}
                    {this.state.readEditMode && (
                      <>
                        <Button size='small' variant='outlined' onClick={()=>this.setState({ readEditMode:false, readEdits:{products:{}}, readAdds:{products:[]}, readRemovals:{products:new Set()} })}>Cancel</Button>
                        <Button size='small' variant='contained' onClick={()=>this.saveNewVersionFromEdits(true)} disabled={!this.hasItemChanges() || this.state.savingItems}>
                          {this.state.savingItems ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                          Save items
                        </Button>
                      </>
                    )}
                  </Stack>
                </Stack>

                {/* Single tab (Products) */}
                <Tabs value={this.state.readTab} onChange={(e,v)=>this.setState({ readTab:v })} sx={{ mt: 1 }}>
                  <Tab label={`Products (${this.state.readDetail._prodCount||0})`} />
                </Tabs>

                {/* Add bar */}
                {this.state.readEditMode && (
                  <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 1, flexWrap:'wrap' }}>
                    <Autocomplete
                      size="small"
                      sx={{ minWidth: 340, flex: '1 1 340px', maxWidth: 520 }}
                      options={this.state.prodOptions}
                      value={this.state.prodSelected}
                      inputValue={this.state.prodAddQuery}
                      getOptionLabel={(option) => option?.name || ''}
                      isOptionEqualToValue={(option, value) =>
                        String(option?.iref || '') === String(value?.iref || '')
                      }
                      onChange={(e, val) => {
                        const q = val?.name || '';
                        this.setState({
                          prodSelected: val,
                          prodAddQuery: q,
                          prodOptions: this.filterProducts(q)
                        });
                      }}
                      onInputChange={(e, val, reason) => {
                        if (reason === 'reset') return;
                        this.setState((prev) => {
                          const selectedName = prev.prodSelected?.name || '';
                          return {
                            prodSelected: selectedName && val !== selectedName ? null : prev.prodSelected,
                            prodAddQuery: val || '',
                            prodOptions: this.filterProducts(val || '')
                          };
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select product"
                          placeholder="Type to filter"
                          size="small"
                        />
                      )}
                    />
                    <TextField size='small' label='Price' value={this.state.prodPriceInput} onChange={(e)=>{ const v=Number(e.target.value); if(v>=0||e.target.value===''){ this.setState({ prodPriceInput: e.target.value }); } }} sx={{ width: 120 }} />
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <Checkbox size='small' checked={this.state.prodActiveInput} onChange={(e)=> this.setState({ prodActiveInput: e.target.checked })} />
                      <Typography variant='body2'>Active</Typography>
                    </Stack>
                    <Button
                      size='small'
                      onClick={this.addNewProduct}
                      disabled={
                        !this.state.prodSelected ||
                        this.state.prodPriceInput === '' ||
                        isNaN(Number(this.state.prodPriceInput)) ||
                        this.existsInCurrent(this.state.prodSelected?.iref)
                      }
                    >
                      {this.existsInCurrent(this.state.prodSelected?.iref) ? 'Already in list' : 'Add'}
                    </Button>
                  </Stack>
                )}

                {/* Products table */}
                <Paper variant='outlined' sx={{ mt: 1, minHeight: 420 }}>
                  {this.state.readLoading ? (
                    <Box sx={{ p: 2 }}><CircularProgress size={24} /> Loading…</Box>
                  ) : (
                    <Table size='small' stickyHeader>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                          <TableCell sx={{ fontWeight: 'bold', color: '#444' }}>Image</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#444' }}>Name</TableCell>
                          {/* <TableCell sx={{ fontWeight: 'bold', color: '#444' }}>SKU</TableCell> */}
                          <TableCell sx={{ fontWeight: 'bold', color: '#444' }}>Categ. / Subcateg.</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#444' }} align='center'>Price</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#444' }}>Active</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#444' }}>Purchased</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#444' }}>Status</TableCell>
                          {this.state.readEditMode && <TableCell />}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* new rows */}
                        {this.state.readEditMode && (this.state.readAdds.products || [])
                          .map((n, idxN) => ({ n, idxN }))
                          .filter(({ n }) => this.matchesAddQuery(n, this.state.prodAddQuery))
                          .map(({ n, idxN }) => (
                          <TableRow key={`newprod_${idxN}`} hover sx={{ backgroundColor: 'rgba(76,175,80,0.08)' }}>
                            <TableCell>-</TableCell>
                            <TableCell><Typography variant='body2'>{n.product_name || n.product_iref}</Typography></TableCell>
                            {/* <TableCell>-</TableCell> */}
                            <TableCell>-</TableCell>
                            <TableCell align='center'>{n.price}</TableCell>
                            <TableCell><Checkbox size='small' checked={!!n.isActive} readOnly /></TableCell>
                            <TableCell>
                              {n.isPurchased ? 'Yes' : 'No'}
                            </TableCell>
                            <TableCell>
                              {n.status ?? '—'}
                            </TableCell>
                            <TableCell><IconButton size='small' onClick={()=> this.removeNewRow('products', idxN)}><CloseIcon fontSize='inherit' /></IconButton></TableCell>
                          </TableRow>
                        ))}
                        {this.acqProductsOf(this.state.readDetail)
                          .map((row, idx) => this.normalizeProductItem(row, this.state.readDetail, idx))
                          .filter(item => this.matchesAddQuery(item, this.state.prodAddQuery))
                          .sort((a,b)=> (a.name || '').localeCompare(b.name || ''))
                          .map((item)=>{
                            const edited = {
                              price: item.price,
                              isActive: item.isActive,
                              ...(this.state.readEdits.products[item.key] || {})
                            };
                            const removed = this.state.readRemovals.products.has(item.key);
                            return (
                              <TableRow key={item.key} hover
                                sx={{
                                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                                  opacity: removed ? 0.5 : 1,
                                  backgroundColor: (this.state.readEditMode && !!this.state.readEdits.products[item.key])
                                    ? 'rgba(255, 255, 200, 0.2)' : undefined
                                }}>
                                <TableCell><Box component='img' alt='No Image' src={item.img} sx={{ width:48, height:48, border:'1px dashed grey' }} /></TableCell>
                                <TableCell>{item.name || item.sku || item.product_iref || '—'}</TableCell>
                                {/* <TableCell>{item.sku}</TableCell> */}
                                <TableCell>
                                  <Stack direction="row" spacing={1}>
                                    {item?.pCategory?.image && (
                                      <Avatar
                                        title={item.pCategory.name}
                                        alt={item.pCategory.name}
                                        src={this.getThumbFromImages([{ image: item.pCategory.image }])}
                                        sx={{ width: 48, height: 48 }}
                                      />
                                    )}
                                    {item?.pSubCategory?.image && (
                                      <Avatar
                                        title={item.pSubCategory.name}
                                        alt={item.pSubCategory.name}
                                        src={this.getThumbFromImages([{ image: item.pSubCategory.image }])}
                                        sx={{ width: 48, height: 48 }}
                                      />
                                    )}
                                  </Stack>
                                </TableCell>
                                <TableCell align='center'>
                                  {this.state.readEditMode ? (
                                    <TextField size='small' value={(edited.price != null ? edited.price : item.price)} onChange={(e)=>{ const v=Number(e.target.value); if(v>=0||e.target.value===''){ this.setEdit('products', item.key, 'price', e.target.value);} }} sx={{ maxWidth: 140, '& .MuiInputBase-input': {textAlign: 'center'} }} />
                                  ) : (item.price)}
                                </TableCell>
                                <TableCell>
                                  <Checkbox size='small' checked={!!(edited.isActive)} disabled={!this.state.readEditMode} onChange={(e)=>this.setEdit('products', item.key, 'isActive', e.target.checked)} />
                                </TableCell>
                                <TableCell>
                                  {item.isPurchased == null ? '—' : (item.isPurchased ? 'Yes' : 'No')}
                                </TableCell>
                                <TableCell>
                                  {item.status != null && item.status !== '' ? item.status : '—'}
                                </TableCell>
                                {this.state.readEditMode && (
                                  <TableCell>
                                    <IconButton size='small' onClick={()=> this.toggleRemoval('products', item.key)}><CloseIcon fontSize='inherit' /></IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  )}
                </Paper>
              </>
            )}
          </DialogContent>
          <DialogActions>
            
            {(this.state.readEditMode && this.hasItemChanges()) && (
              <Button size='small' variant='contained' onClick={()=> this.saveNewVersionFromEdits(true)} disabled={this.state.savingItems}>
                {this.state.savingItems ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                Save items & Close
              </Button>
            )}
            {(!this.hasItemChanges() && this.hasMetaChanges()) && (
              <Button size='small' variant='contained' onClick={()=> this.saveMeta(true)}>
                Save meta & Close
              </Button>
            )}
            <Button size='small' variant='outlined' startIcon={<CloudDownloadIcon/>} onClick={this.exportAcqDetailCSV}>
              Export CSV
            </Button>
            <Button onClick={this.handleCloseRead}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Confirm close (unsaved changes) */}
        <Dialog open={this.state.confirmCloseOpen} onClose={() => {}}>
          <DialogTitle>Discard changes?</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2">You have unsaved changes. If you close now, they will be lost.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={()=> this.setState({ confirmCloseOpen:false })}>Cancel</Button>
            <Button color="error" variant="contained" onClick={()=> this.setState({ confirmCloseOpen:false, readOpen:false, readEditMode:false, metaDirty:false })}>Discard</Button>
          </DialogActions>
        </Dialog>

        {/* COMPARE DIALOG (Products only) */}
        <Dialog
          open={this.state.cmpOpen}
          // onClose={()=> this.setState({ cmpOpen:false })}
          onClose={() => {}}
          fullWidth
          maxWidth="lg"
          PaperProps={{ sx:{ height:'75vh', display:'flex', flexDirection:'column' } }}
        >
          <DialogTitle sx={{ pb: 0.5, pt: 1.5 }}>
            {this.nameOf(this.state.cmpBase?.company,  this.state.companies)}
            {' · '}
            Compare v{this.state.cmpBase?.version} ↔ v{this.state.cmpOther?.version}
            <IconButton aria-label="close" onClick={()=> this.setState({ cmpOpen:false })} sx={{ position:'absolute', right:8, top:8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ flex:1, overflowY:'auto', p: 0 }}>
            <Stack direction="row" spacing={2} sx={{ m: 2 }} alignItems="center">
              <Chip label={`Products: +${this.state.cmpDiff?.products?.summary?.added || 0} / -${this.state.cmpDiff?.products?.summary?.removed || 0} / Δ${this.state.cmpDiff?.products?.summary?.changed || 0}`} />
            </Stack>
        
            {/* Added */}
            <Paper variant="outlined" sx={{ m:2, borderColor:'success.main' }}>
              <Box sx={{ bgcolor:'success.light', px:1, py:0.5 }}><Typography variant="subtitle2">Added</Typography></Box>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  <TableCell>Name</TableCell><TableCell>SKU</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Currency</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {(this.state.cmpDiff?.products?.added || []).map((r, i)=>(
                    <TableRow key={`pa_${i}`}>
                      <TableCell>{this.displayName(r)}</TableCell>
                      <TableCell>{this.displaySku(r)}</TableCell>
                      <TableCell align="right">{(r.price != null && r.price !== '' ? r.price : '—')}</TableCell>
                      <TableCell>{r.isActive ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{toCurrencyName(r.currency ?? this.state.cmpBase?.currency)}</TableCell>
                    </TableRow>
                  ))}
                  {(!this.state.cmpDiff?.products?.added?.length) && (
                    <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">None</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
        
            {/* Removed */}
            <Paper variant="outlined" sx={{ m:2, borderColor:'error.main' }}>
              <Box sx={{ bgcolor:'error.light', px:1, py:0.5 }}><Typography variant="subtitle2">Removed</Typography></Box>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  <TableCell>Name</TableCell><TableCell>SKU</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Currency</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {(this.state.cmpDiff?.products?.removed || []).map((r, i)=>(
                    <TableRow key={`pr_${i}`}>
                      <TableCell>{this.displayName(r)}</TableCell>
                      <TableCell>{this.displaySku(r)}</TableCell>
                      <TableCell align="right">{r.price}</TableCell>
                      <TableCell>{r.isActive ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{toCurrencyName(r.currency ?? this.state.cmpOther?.currency)}</TableCell>
                    </TableRow>
                  ))}
                  {(!this.state.cmpDiff?.products?.removed?.length) && (
                    <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">None</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
        
            {/* Changed */}
            <Paper variant="outlined" sx={{ m:2, borderColor:'warning.main' }}>
              <Box sx={{ bgcolor:'warning.light', px:1, py:0.5 }}><Typography variant="subtitle2">Changed</Typography></Box>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  <TableCell>Name</TableCell><TableCell>SKU</TableCell>
                  <TableCell align="right">Prev → Curr (Price)</TableCell>
                  <TableCell>Prev → Curr (Active)</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {(this.state.cmpDiff?.products?.changed || []).map((r, i)=>(
                    <TableRow key={`pc_${i}`}>
                      <TableCell>{this.displayName(r.to) || this.displayName(r.from)}</TableCell>
                      <TableCell>{this.displaySku(r.to)  || this.displaySku(r.from)}</TableCell>
                      <TableCell align="right" sx={{ color: r.from.price !== r.to.price ? 'warning.dark' : 'text.primary' }}>
                        {r.from.price} → {r.to.price}
                      </TableCell>
                      <TableCell>{(r.from.isActive?'Yes':'No')} → {(r.to.isActive?'Yes':'No')}</TableCell>
                    </TableRow>
                  ))}
                  {(!this.state.cmpDiff?.products?.changed?.length) && (
                    <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">None</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          </DialogContent>
        </Dialog>

        {/* Confirm Discard Dialog */}
        <Dialog open={this.state.confirmCloseOpen} onClose={() => {}}>
          <DialogTitle>Discard changes?</DialogTitle>
          <DialogContent dividers>
            <Typography variant='body2'>You have unsaved changes. Are you sure you want to close without saving?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>this.setState({ confirmCloseOpen:false })}>Cancel</Button>
            <Button color='error' variant='contained' onClick={this.discardChangesAndClose}>Discard</Button>
          </DialogActions>
        </Dialog>

        {/* Snack */}
        <Snackbar
          open={this.state.snack.open}
          autoHideDuration={4000}
          onClose={()=> this.setState({ snack: { ...this.state.snack, open:false } })}
          anchorOrigin={{ vertical:'bottom', horizontal:'center' }}
        >
          <Alert onClose={()=> this.setState({ snack: { ...this.state.snack, open:false } })} severity={this.state.snack.severity} sx={{ width: '100%' }}>
            {this.state.snack.message}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}